import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, Inbox, Zap, LogOut, Menu, X, FolderKanban, Package, Settings, Wrench, BookOpen, Users, CalendarDays, Globe } from "lucide-react";
import { Toaster } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";
import { usePageMeta } from "../Layout";
import { FileCategoriesProvider } from "../../contexts/FileCategoriesContext";
// WP6-B.5 §5.1 — admin surface is noindex at the page level too (the
// X-Robots-Tag HTTP header set in `backend/server.py` middleware is
// belt-and-braces; this meta tag covers the SPA DOM for crawlers that
// render JS before honouring the HTTP header).
// Also pulls in the admin-only tiptap RTE styles for the Marketing CMS.
import "../../styles/rte.css";

const NAV = [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
    // §Sidebar Badge Cleanup — Projects is the only badge-bearing item.
    //   Value = active projects currently on WS1 (Enquiry & Qualification).
    { to: "/admin/projects", label: "Projects", icon: FolderKanban, badgeKey: "ws1_active" },
    { to: "/admin/planner", label: "Planner", icon: CalendarDays },
    { to: "/admin/service-packages", label: "Service Packages", icon: Package },
    { to: "/admin/content-library", label: "Content Library", icon: BookOpen },
    { to: "/admin/marketing-website", label: "Marketing Website", icon: Globe },
];

// Phase D0 — locked Administration IA: Configuration + Tools (both active).
// D5 addition: `Users` — minimal platform-user administration for the
// existing Admin (view + edit first_name/last_name only).
// Meta events is relocated from the main sidebar into the Tools landing
// page as an operational utility; the underlying route is retained.
const NAV_ADMIN = [
    { to: "/admin/config", label: "Configuration", icon: Settings },
    { to: "/admin/tools", label: "Tools", icon: Wrench },
    { to: "/admin/users", label: "Users", icon: Users },
];

function Badge({ count, tone = "charcoal" }) {
    // §Sidebar Badge Cleanup — render `0` explicitly (matches the
    //   workspace's Pending 0 convention).  Only `null`/`undefined`
    //   hides the pill entirely, so items that shouldn't carry a
    //   badge at all just omit their `badgeKey`.
    if (count === null || count === undefined) return null;
    const cls = tone === "terracotta"
        ? "bg-terracotta text-offwhite"
        : "bg-offwhite/20 text-offwhite";
    return <span className={`ml-auto text-[11px] font-manrope font-bold px-1.5 py-0.5 rounded ${cls}`} data-testid={`sidebar-badge-${tone}`}>{count}</span>;
}

export default function AdminLayout() {
    usePageMeta({ robots: "noindex, nofollow, noarchive, nosnippet" });
    const { user, isLoading, logout } = useAuth();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [drawerOpen, setDrawerOpen] = useState(false);
    // §Sidebar Badge Cleanup — only the Projects badge remains.
    //   `ws1_active` is the count of Active projects on WS1.
    const [counts, setCounts] = useState({ ws1_active: null });

    // Redirect to /admin/login if unauthenticated.
    useEffect(() => {
        if (!isLoading && !user) {
            navigate("/admin/login", { replace: true, state: { from: pathname } });
        }
    }, [isLoading, user, navigate, pathname]);

    // Fetch sidebar badge count once per mount.  The three legacy
    //   fetches (/admin/dashboard, meta-events, projects?shortcut=…)
    //   were feeding badges that no longer render.
    useEffect(() => {
        if (!user) return;
        let cancelled = false;
        (async () => {
            try {
                const r = await api.get("/admin/projects/counts");
                if (cancelled) return;
                setCounts({
                    ws1_active: r.data?.ws1_active ?? 0,
                });
            } catch { /* leave default null → badge simply hidden */ }
        })();
        return () => { cancelled = true; };
    }, [user]);

    useEffect(() => { setDrawerOpen(false); }, [pathname]);

    const doLogout = async () => {
        await logout();
        navigate("/admin/login", { replace: true });
    };

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-offwhite text-charcoal font-inter">
                <div className="opacity-60">Loading…</div>
            </div>
        );
    }
    if (!user) return null; // will redirect via effect

    // §Sidebar Badge Cleanup — only WS1-active projects gets a badge.
    //   Every other NAV item omits `badgeKey`, so this returns null.
    const badgeFor = (key) => key === "ws1_active"
        ? <Badge count={counts.ws1_active} />
        : null;

    const Nav = ({ onNavigate }) => (
        <nav className="flex flex-col gap-1 mt-6">
            {NAV.map((n) => (
                <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.end}
                    onClick={onNavigate}
                    className={({ isActive }) =>
                        "relative flex items-center gap-3 px-6 py-3 font-manrope font-semibold text-[14px] transition-colors " +
                        (isActive ? "text-offwhite bg-offwhite/[0.06]" : "text-offwhite/70 hover:text-offwhite hover:bg-offwhite/[0.03]")
                    }
                    data-testid={`sidebar-link-${n.label.toLowerCase().replace(/\s+/g,'-')}`}
                >
                    {({ isActive }) => (
                        <>
                            {isActive && <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-terracotta" />}
                            <n.icon size={17} className="shrink-0" />
                            <span>{n.label}</span>
                            {badgeFor(n.badgeKey)}
                        </>
                    )}
                </NavLink>
            ))}
            {/* V2 §5 — Administration section */}
            <div className="mt-4 px-6 pb-1 font-manrope text-[10px] uppercase tracking-[0.16em] text-offwhite/40"
                 data-testid="sidebar-group-administration">
                Administration
            </div>
            {NAV_ADMIN.map((n) => (
                n.disabled ? (
                    <div key={n.label}
                         title={n.hint || ""}
                         className="relative flex items-center gap-3 px-6 py-3 font-manrope font-semibold text-[14px] text-offwhite/30 cursor-not-allowed"
                         data-testid={`sidebar-admin-${n.label.toLowerCase()}-disabled`}>
                        <n.icon size={17} className="shrink-0" />
                        <span>{n.label}</span>
                        <span className="ml-auto text-[10px] uppercase tracking-wide text-offwhite/25">Coming later</span>
                    </div>
                ) : (
                    <NavLink
                        key={n.to}
                        to={n.to}
                        onClick={onNavigate}
                        className={({ isActive }) =>
                            "relative flex items-center gap-3 px-6 py-3 font-manrope font-semibold text-[14px] transition-colors " +
                            (isActive ? "text-offwhite bg-offwhite/[0.06]" : "text-offwhite/70 hover:text-offwhite hover:bg-offwhite/[0.03]")
                        }
                        data-testid={`sidebar-admin-${n.label.toLowerCase()}`}
                    >
                        {({ isActive }) => (
                            <>
                                {isActive && <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-terracotta" />}
                                <n.icon size={17} className="shrink-0" />
                                <span>{n.label}</span>
                            </>
                        )}
                    </NavLink>
                )
            ))}
        </nav>
    );

    return (
        <div className="min-h-screen bg-offwhite text-charcoal">
            {/* Sidebar (desktop) */}
            <aside className="hidden md:flex fixed top-0 left-0 bottom-0 w-[268px] bg-charcoal text-offwhite flex-col z-30">
                <div className="px-6 pt-6 pb-2" data-testid="admin-sidebar-brand">
                    <img
                        src="/SG4_Logo_Light.png"
                        alt="Studio G4"
                        data-testid="admin-sidebar-logo"
                        className="w-full max-w-[180px] h-auto select-none"
                    />
                </div>
                <div className="px-6 mb-3">
                    <span
                        data-testid="admin-sidebar-tag"
                        className="inline-block rounded-md bg-white text-[#A8543F] font-manrope font-semibold text-[12px] px-3 py-1.5">
                        Admin Portal
                    </span>
                </div>
                <Nav />
                <div className="mt-auto px-6 py-5 border-t border-offwhite/10">
                    <div className="text-[12.5px] text-offwhite/60 break-all">{user.email}</div>
                    <button onClick={doLogout} className="mt-2 flex items-center gap-2 text-[13px] text-offwhite/80 hover:text-terracotta font-manrope font-semibold" data-testid="sidebar-logout">
                        <LogOut size={14} /> Sign out
                    </button>
                </div>
            </aside>

            {/* Mobile top bar */}
            <div className="md:hidden sticky top-0 z-30 bg-charcoal text-offwhite flex items-center justify-between px-4 h-14">
                <div className="flex items-center gap-2" data-testid="admin-mobile-brand">
                    <img
                        src="/SG4_Logo_Light.png"
                        alt="Studio G4"
                        className="h-9 w-auto select-none"
                    />
                    <span
                        className="rounded-md bg-white text-[#A8543F] font-manrope font-semibold text-[10px] px-2 py-0.5"
                        data-testid="admin-mobile-tag">
                        Admin Portal
                    </span>
                </div>
                <button onClick={() => setDrawerOpen(true)} className="p-2" data-testid="admin-mobile-menu-open">
                    <Menu size={22} />
                </button>
            </div>

            {drawerOpen && (
                <div className="md:hidden fixed inset-0 z-40 bg-charcoal text-offwhite flex flex-col" data-testid="admin-drawer">
                    <div className="flex items-center justify-between px-4 h-14">
                        <div className="flex items-center gap-2">
                            <img
                                src="/SG4_Logo_Light.png"
                                alt="Studio G4"
                                className="h-9 w-auto select-none"
                            />
                            <span className="rounded-md bg-white text-[#A8543F] font-manrope font-semibold text-[10px] px-2 py-0.5">
                                Admin Portal
                            </span>
                        </div>
                        <button onClick={() => setDrawerOpen(false)} className="p-2"><X size={22} /></button>
                    </div>
                    <Nav onNavigate={() => setDrawerOpen(false)} />
                    <div className="mt-auto px-6 py-5 border-t border-offwhite/10">
                        <div className="text-[12.5px] text-offwhite/60">{user.email}</div>
                        <button onClick={doLogout} className="mt-2 flex items-center gap-2 text-[13px] text-offwhite/80 font-manrope font-semibold">
                            <LogOut size={14} /> Sign out
                        </button>
                    </div>
                </div>
            )}

            {/* Main content */}
            <main className="md:ml-[268px] p-6 md:p-10" data-testid="admin-main">
                <FileCategoriesProvider>
                    <Outlet />
                </FileCategoriesProvider>
            </main>

            <Toaster position="bottom-right" richColors closeButton />
        </div>
    );
}
